/**
 * Game State Manager Service
 * 
 * Manages game state persistence using IndexedDB.
 * Allows saving, loading, and checking for saved games.
 */

import { debugLog, debugError, debugWarn, debugTable, debugGroup, debugGroupEnd } from '../utils/debugUtils';
const DB_NAME = 'RondaYBacigaDB';
const DB_VERSION = 1;
const STORE_NAME = 'savedGames';

class GameStateManager {
    constructor() {
        this.db = null;
        this.isReady = false;
        this._initPromise = null;
    }

    /**
     * Initialize the IndexedDB database
     * 
     * @returns {Promise<boolean>} True if initialization successful
     */
    async init() {
        if (this.isReady) return true;
        if (this._initPromise) return this._initPromise;

        this._initPromise = new Promise((resolve, reject) => {
            try {
                const request = indexedDB.open(DB_NAME, DB_VERSION);

                request.onerror = () => {
                    debugError('[GameStateManager] Failed to open database:', request.error);
                    reject(request.error);
                };

                request.onsuccess = () => {
                    this.db = request.result;
                    this.isReady = true;
                    debugLog('[GameStateManager] Database initialized');
                    resolve(true);
                };

                request.onupgradeneeded = (event) => {
                    const db = event.target.result;

                    // Create object store for saved games
                    if (!db.objectStoreNames.contains(STORE_NAME)) {
                        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
                        debugLog('[GameStateManager] Object store created');
                    }
                };
            } catch (error) {
                debugError('[GameStateManager] IndexedDB not available:', error);
                reject(error);
            }
        });

        return this._initPromise;
    }

    /**
     * Save the current game state
     * 
     * @param {string} gameType - 'ronda' or 'baciga'
     * @param {Object} state - The game state to save
     * @returns {Promise<boolean>} True if save successful
     */
    async saveGameState(gameType, state) {
        try {
            await this.init();

            const savedGame = {
                id: 'current',
                gameType,
                state,
                savedAt: new Date().toISOString(),
                version: process.env.REACT_APP_VERSION || '1.0.0',
            };

            return new Promise((resolve, reject) => {
                const transaction = this.db.transaction([STORE_NAME], 'readwrite');
                const store = transaction.objectStore(STORE_NAME);
                const request = store.put(savedGame);

                request.onsuccess = () => {
                    debugLog('[GameStateManager] Game saved successfully');
                    resolve(true);
                };

                request.onerror = () => {
                    debugError('[GameStateManager] Failed to save game:', request.error);
                    reject(request.error);
                };
            });
        } catch (error) {
            debugError('[GameStateManager] Error saving game:', error);
            return false;
        }
    }

    /**
     * Load the saved game state
     * 
     * @returns {Promise<Object|null>} The saved game data or null if none exists
     */
    async loadGameState() {
        try {
            await this.init();

            return new Promise((resolve, reject) => {
                const transaction = this.db.transaction([STORE_NAME], 'readonly');
                const store = transaction.objectStore(STORE_NAME);
                const request = store.get('current');

                request.onsuccess = () => {
                    const result = request.result;
                    if (result) {
                        debugLog('[GameStateManager] Game loaded:', result.gameType);
                        debugLog('[GameStateManager] Raw result from IndexedDB:', result);
                        debugLog('[GameStateManager] result.state type:', typeof result.state);

                        // Si result.state es una cadena JSON, parsearlo
                        if (typeof result.state === 'string') {
                            debugLog('[GameStateManager] Parsing state from JSON string...');
                            try {
                                result.state = JSON.parse(result.state);
                                debugLog('[GameStateManager] State parsed successfully');
                            } catch (e) {
                                debugError('[GameStateManager] Failed to parse state JSON:', e);
                            }
                        }

                        resolve(result);
                    } else {
                        resolve(null);
                    }
                };

                request.onerror = () => {
                    debugError('[GameStateManager] Failed to load game:', request.error);
                    reject(request.error);
                };
            });
        } catch (error) {
            debugError('[GameStateManager] Error loading game:', error);
            return null;
        }
    }

    /**
     * Check if a saved game exists
     * 
     * @returns {Promise<boolean>} True if a saved game exists
     */
    async hasSavedGame() {
        const savedGame = await this.loadGameState();
        return savedGame !== null;
    }

    /**
     * Get saved game info without loading full state
     * 
     * @returns {Promise<Object|null>} Basic info about saved game or null
     */
    async getSavedGameInfo() {
        const savedGame = await this.loadGameState();
        if (!savedGame) return null;

        return {
            gameType: savedGame.gameType,
            savedAt: savedGame.savedAt,
            version: savedGame.version,
        };
    }

    /**
     * Clear the saved game
     * 
     * @returns {Promise<boolean>} True if cleared successfully
     */
    async clearSavedGame() {
        try {
            await this.init();

            return new Promise((resolve, reject) => {
                const transaction = this.db.transaction([STORE_NAME], 'readwrite');
                const store = transaction.objectStore(STORE_NAME);
                const request = store.delete('current');

                request.onsuccess = () => {
                    debugLog('[GameStateManager] Saved game cleared');
                    resolve(true);
                };

                request.onerror = () => {
                    debugError('[GameStateManager] Failed to clear saved game:', request.error);
                    reject(request.error);
                };
            });
        } catch (error) {
            debugError('[GameStateManager] Error clearing saved game:', error);
            return false;
        }
    }
}

// Export singleton instance
const gameStateManager = new GameStateManager();

// Also export the class for testing
export { GameStateManager };
export default gameStateManager;
